import type { EventPageEvent } from '../data/types'
import { sanitizeRichText } from '../data/richText'
import styles from './VenueSection.module.css'

// Icons match MobileVenue.dc.html / Desktop.dc.html literally (see feature-161 notes on treating
// the mockup as the port spec, not something to reinterpret from memory).
function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  )
}
function PhoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
    </svg>
  )
}
function EmailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  )
}
function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12l5 5L20 7" />
    </svg>
  )
}

export function VenueSection({ event, isMobile }: { event: EventPageEvent; isMobile: boolean }) {
  const venueName = event.altVenue || event.trackName
  const address1 = event.altVenue ? event.altAddress1 : event.address1
  const address2 = event.altVenue ? event.altAddress2 : event.address2
  const city = event.altVenue ? event.altCity : event.city
  const state = event.altVenue ? event.altState : event.state
  const zip = event.altVenue ? event.altZip : event.zip
  const fullAddress = [address1, address2, city && state ? `${city}, ${state} ${zip}` : city || state].filter(Boolean).join(', ')
  const mapsHref = fullAddress ? `https://maps.google.com/maps?q=${encodeURIComponent(fullAddress)}` : undefined
  const telHref = event.phone ? `tel:${event.phone}` : undefined
  const mailHref = event.email ? `mailto:${event.email}` : undefined
  const contactName = event.contactFirstName ? `${event.contactFirstName} ${event.contactLastName}` : ''

  return (
    <div className={styles.grid}>
      <div className={styles.card}>
        {/* Real map tiles need a Google Maps Static API key, which nothing in this app wires up yet
            (CLAUDE.md's "External Service API Keys" - only Maps JS is hardcoded in legacy templates,
            not Static Maps). Matching the mockup literally: a tappable placeholder that goes straight
            to Google Maps directions, same destination as the "Directions" button below. */}
        {mapsHref ? (
          <a href={mapsHref} target="_blank" rel="noreferrer" className={styles.mapPlaceholder}>
            Static map · tap for directions
          </a>
        ) : null}
        <div className={styles.cardBody}>
          <div className={styles.venueName}>{venueName}</div>
          {fullAddress ? <div className={styles.address}>{fullAddress}</div> : null}
          {isMobile ? (
            contactName ? <div className={styles.contactLine}>Contact: {contactName}</div> : null
          ) : (
            <div className={styles.contactLine}>
              {contactName ? `${contactName} · ` : ''}
              {event.phone ? <a href={telHref}>{event.phone}</a> : null}
              {event.phone && event.email ? ' · ' : ''}
              {event.email ? <a href={mailHref}>{event.email}</a> : null}
            </div>
          )}
          {!isMobile && mapsHref ? (
            <a href={mapsHref} target="_blank" rel="noreferrer" className={styles.directions}>
              Get directions
            </a>
          ) : null}
        </div>
        {isMobile ? (
          <div className={styles.actionRow}>
            {mapsHref ? (
              <a href={mapsHref} target="_blank" rel="noreferrer" className={styles.actionPrimary}>
                <PinIcon />
                Directions
              </a>
            ) : null}
            {telHref ? (
              <a href={telHref} className={styles.actionSecondary}>
                <PhoneIcon />
                Call
              </a>
            ) : null}
            {mailHref ? (
              <a href={mailHref} className={styles.actionSecondary}>
                <EmailIcon />
                Email
              </a>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Mobile folds amenities directly into the Venue card as bare checkmark chips
          (MobileVenue.dc.html); desktop keeps them as the separate rail AmenitiesCard
          (Desktop.dc.html) - see the App.tsx comment where each is rendered. */}
      {isMobile && event.trackAmenities.length ? (
        <div className={styles.amenityChips}>
          {event.trackAmenities.map((a) => (
            <span key={a} className={styles.amenityChip}>
              <CheckIcon />
              {a}
            </span>
          ))}
        </div>
      ) : null}

      {event.hotelInfo ? (
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <div className={styles.hotelLabel}>Host hotel</div>
            {/* Rich text (Quill/CKEditor), same trust boundary as event.description - see App.tsx. */}
            {/* eslint-disable-next-line react/no-danger */}
            <div className={styles.hotelInfo} dangerouslySetInnerHTML={{ __html: sanitizeRichText(event.hotelInfo) }} />
          </div>
        </div>
      ) : null}
    </div>
  )
}
